import { PromiseExtended } from 'dexie';
import { makeClearPublisher } from 'serviceWorker/broadcasts/publishers';
import { documentationFilteringCacheManager } from 'serviceWorker/repository/documentation/documentationFilteringCacheManager/index';
import { documentationContract } from 'shared/domain/documentation/documentationContract';
import {
  DocumentationEntity,
  DocumentationInsertEntity,
  DocumentationLocalInsertEntity,
  DocumentationVersionChangesOutDto
} from 'serviceWorker/repository/documentation/entity';
import { documentationInDtoToCreateEntity } from 'serviceWorker/repository/documentation/mappings';
import {
  isSyncStatusPendingDelete,
  isSyncStatusSuccess,
} from 'shared/domain/entitySyncStatus/syncStatus';
import { DocumentationInDto } from 'shared/dtos/in/documentation';
import { Changes } from 'shared/types/commonEntities';
import { LogLevel } from 'shared/types/logger';
import {
  isUploadStatusSuccess
} from 'shared/types/uploadStatus';
import { nowISO } from 'shared/utils/date/dates';
import { normalizeForSearch } from 'shared/utils/search';
import {
  makeDefaultCount,
  makeDefaultGetMany,
  makeDefaultGetManyByIds,
  makeDefaultGetOne,
  makeDefaultRemoveBatch,
  makeGetManyUndeleted,
} from '../defaultDaoFactories';
import { entityDbClearFactory } from '../entityDbClearFactory';
import {
  getErrorMsg,
  isDexieConstraintError,
  logWithRethrow,
  Operation,
} from '../helpers';
import db from '../index';
import { wrapQuery } from '../queryWrapper';
import { clear as documentationsServiceClear } from './documentationsService';
import { assignRemoteIdFactory } from '../assignRemoteIdFactory';

const publishClearDocumentations = makeClearPublisher(documentationContract);

/** Cleared documentations drop their cached filtering before the page is told. */
function publishDocumentationsCleared(): void {
  documentationFilteringCacheManager.invalidateAllDocumentationFilteringCaches();
  publishClearDocumentations();
}

export const clear = entityDbClearFactory(
  db,
  ['documentations', 'documentationsService'],
  () => db.documentations.clear(),
  documentationsServiceClear,
  publishDocumentationsCleared
);

const update = wrapQuery(
  db,
  (
    documentationFindStrategy: CallableFunction,
    data: DocumentationInsertEntity
  ): PromiseExtended<any> => {
    return documentationFindStrategy().then(
      (documentation: DocumentationEntity) => {
        if (!documentation) {
          throw new Error('Cannot find document to update');
        } else {
          const documentationWithLocalData =
            keepLocalDataOnUnuploadedStatus(data, documentation);

          return db.documentations.update(
            documentation.localId,
            documentationWithLocalData
          );
        }
      }
    );
  }
);

export const quantity = makeDefaultCount<DocumentationEntity>(
  db,
  db.documentations
);

export const get = makeDefaultGetMany<DocumentationEntity>(
  db,
  db.documentations
);

export const getUndeleted = makeDefaultGetMany<DocumentationEntity>(
  db,
  db.documentations,
  { deleted: 0 }
);

export const getByQuery = makeDefaultGetMany<DocumentationEntity>(
  db,
  db.documentations
);

export const getUndeletedByParentId = makeGetManyUndeleted<
  string,
  DocumentationEntity
>(db, db.documentations, 'parentId');

export const getOneByRemote = makeDefaultGetOne<
  string,
  DocumentationEntity
>(db, db.documentations, '_id');

export const getOne = makeDefaultGetOne<number, DocumentationEntity>(
  db,
  db.documentations,
  'localId'
);
export const getByIds = makeDefaultGetManyByIds<
  number,
  DocumentationEntity
>(db, db.documentations, 'localId');

export const getSearched = wrapQuery(
  db,
  async (searchPhrase: string): Promise<DocumentationEntity[]> => {
    return db.documentations
      .toArray()
      .then((docs) => {
        return docs.filter((doc: DocumentationEntity) => {
          return (
            normalizeForSearch(doc.name).includes(
              normalizeForSearch(searchPhrase)
            ) ||
            normalizeForSearch(doc.number || '').includes(
              normalizeForSearch(searchPhrase)
            )
          );
        });
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.filter, 'documentations'),
          errorObj: e,
          additionalInfo: { query: { searchPhrase } },
        })
      );
  }
);

const upsertBatch = wrapQuery(
  db,
  async (data: DocumentationInDto[]): Promise<number> => {
    if (data.length === 0) return Promise.resolve(0);

    const entityMap = new Map<string, DocumentationInsertEntity>();
    for (const doc of data) {
      const entity = documentationInDtoToCreateEntity(doc);
      if (entity._id) {
        entityMap.set(entity._id, entity);
      }
    }
    const uniqueEntities = Array.from(entityMap.values());
    const incomingIds = Array.from(entityMap.keys());

    if (incomingIds.length === 0) return Promise.resolve(0);

    const existingRecords = await db.documentations
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    const existingMap = new Map<string, DocumentationEntity>();
    for (const record of existingRecords) {
      if (record._id) {
        existingMap.set(record._id, record);
      }
    }

    const entitiesToUpsert = uniqueEntities.map((entity) => {
      const existing = existingMap.get(entity._id!);
      if (existing) {
        const preserved = keepLocalDataOnUnuploadedStatus(
          entity,
          existing
        );
        return { ...preserved, localId: existing.localId };
      }
      return entity;
    });

    return (
      db.documentations
        // @ts-ignore localId may or may not be present
        .bulkPut(entitiesToUpsert)
        .catch((e) => {
          logWithRethrow({
            logLevel: LogLevel.INFO,
            msg: getErrorMsg(Operation.upsertBatch, 'documentations'),
            errorObj: e,
            additionalInfo: { query: { data } },
          });
        })
    );
  }
);

export const fastAddBatch = wrapQuery(
  db,
  async (data: DocumentationInDto[]): Promise<number> => {
    if (data.length === 0) return Promise.resolve(0);

    const entityMap = new Map<string, DocumentationInsertEntity>();
    for (const doc of data) {
      const entity = documentationInDtoToCreateEntity(doc);
      if (entity._id) {
        entityMap.set(entity._id, entity);
      }
    }
    const incomingIds = Array.from(entityMap.keys());

    if (incomingIds.length === 0) return Promise.resolve(0);

    const existingRecords = await db.documentations
      .where('_id')
      .anyOf(incomingIds)
      .toArray();

    const existingIdSet = new Set(existingRecords.map((r) => r._id));
    const newEntities = Array.from(entityMap.values()).filter(
      (e) => !existingIdSet.has(e._id!)
    );

    if (newEntities.length === 0) return Promise.resolve(0);

    try {
      return await db.documentations
        // @ts-ignore DocumentationEntityToCreate localId is an autoincrement
        .bulkPut(newEntities);
    } catch (e: unknown) {
      if (isDexieConstraintError(e)) {
        return upsertBatch(data);
      }
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.upsertBatch, 'documentations'),
        errorObj: e,
        additionalInfo: { query: { data } },
      });
      return 0;
    }
  }
);

export const addBatch = upsertBatch;
export const updateBatch = upsertBatch;

export const addLocal = wrapQuery(
  db,
  (data: DocumentationLocalInsertEntity): PromiseExtended<number> => {
    // @ts-ignore DocumentationEntityToCreate localId is an autoincrement
    return db.documentations.add(data).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.add, 'documentations'),
        errorObj: e,
        additionalInfo: { query: { data } },
      })
    );
  }
);

export const removeBatch = makeDefaultRemoveBatch<
  string,
  DocumentationEntity
>(db, db.documentations, '_id');

export const remove = wrapQuery(
  db,
  async (id: number): Promise<unknown> => {
    return db.documentations.delete(id).catch((e) =>
      logWithRethrow({
        logLevel: LogLevel.INFO,
        msg: getErrorMsg(Operation.remove, 'documentations'),
        errorObj: e,
        additionalInfo: { query: { id } },
      })
    );
  }
);

export const updateOne = wrapQuery(
  db,
  async (
    localId: number,
    changes: Changes<DocumentationEntity>,
    options?: { skipModifyTime: boolean }
  ): Promise<any> => {
    const modifiedAt = options?.skipModifyTime
      ? {}
      : { modifiedAt: nowISO() };
    return db.documentations
      .update(localId, { ...changes, ...modifiedAt })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documentations'),
          errorObj: e,
          additionalInfo: { query: changes },
        })
      );
  }
);

export const updateOneVersion = wrapQuery(
  db,
  async (
    localId: number,
    versionId: string,
    changes: DocumentationVersionChangesOutDto
  ): Promise<any> => {
    const documentation = await db.documentations.get(localId);
    if (!documentation) {
      throw new Error('Documentation not found');
    }

    const versionIndex = documentation.versions.findIndex(
      (version) => version._id === versionId
    );
    if (versionIndex === -1) {
      throw new Error('Version not found');
    }

    const updatedVersions = [...documentation.versions];
    updatedVersions[versionIndex] = {
      ...updatedVersions[versionIndex],
      ...changes,
      modifiedAt: nowISO(),
    };

    return db.documentations
      .update(localId, {
        versions: updatedVersions,
        modifiedAt: nowISO(),
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documentations'),
          errorObj: e,
          additionalInfo: { query: { localId, versionId, changes } },
        })
      );
  }
);

export const updateFirstVersionIds = wrapQuery(
  db,
  async (
    localId: number,
    versionId: string,
    remoteVersionId: string
  ): Promise<number> => {
    return db.documentations
      .where('localId')
      .equals(localId)
      .modify((documentation) => {
        if (
          documentation.versions &&
          documentation.versions.length > 0 &&
          documentation.versions[0]
        ) {
          // @ts-ignore
          documentation.versions[0]._id = versionId;
          documentation.versions[0].remoteVersionId = remoteVersionId;
        }
      })
      .catch((e) =>
        logWithRethrow({
          logLevel: LogLevel.INFO,
          msg: getErrorMsg(Operation.updateOne, 'documentations'),
          errorObj: e,
          additionalInfo: {
            query: { localId, versionId, remoteVersionId },
          },
        })
      );
  }
);

function keepLocalDataOnUnuploadedStatus(
  documentationToInsert: DocumentationInsertEntity,
  existingEntity: DocumentationEntity
): DocumentationInsertEntity {
  const versionToInsert = documentationToInsert.versions[0];
  const existingVersion = existingEntity.versions[0];
  if (!versionToInsert && existingVersion) {
    documentationToInsert.versions[0] = existingVersion;
    return documentationToInsert;
  }
  if (!versionToInsert) {
    return documentationToInsert;
  }
  if (
    existingVersion &&
    documentationToInsert._id &&
    isSyncStatusSuccess(existingEntity.syncStatus) &&
    isSyncStatusSuccess(existingVersion.syncStatus) &&
    isUploadStatusSuccess(versionToInsert.uploadStatus) &&
    existingVersion.localData
  ) {
    documentationToInsert.versions[0].localData = undefined;
  } else {
    documentationToInsert.versions[0].localData =
      existingVersion.localData;
  }

  if (
    isSyncStatusPendingDelete(existingEntity.syncStatus) &&
    !documentationToInsert.deleted
  ) {
    documentationToInsert.deleted = 1;
  }

  return documentationToInsert;
}

/** The server id of a documentation created here; drops a copy a sync stored first (PT-7278). */
export const assignRemoteId = assignRemoteIdFactory(db, 'documentations', db.documentations);
